import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, FileText, Shield, Scale } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface LegalPageData {
  id: string;
  slug: string;
  title: string;
  content: string;
  meta_description: string;
  updated_at: string;
}

const LegalPage: React.FC = () => {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const [page, setPage] = useState<LegalPageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('legal_pages')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setPage(data);
        } else {
          toast.error('Page non trouvée');
        }
      } catch (error) {
        console.error('Error fetching legal page:', error);
        toast.error('Erreur lors du chargement de la page');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }
  }, [slug]);

  const getIcon = () => {
    switch (slug) {
      case 'legal':
        return <FileText className="w-8 h-8 text-primary-600" />;
      case 'privacy':
        return <Shield className="w-8 h-8 text-primary-600" />;
      case 'terms':
        return <Scale className="w-8 h-8 text-primary-600" />;
      default:
        return <FileText className="w-8 h-8 text-primary-600" />;
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Page non trouvée</h1>
            <p className="text-gray-600 mb-8">La page que vous recherchez n'existe pas.</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <Helmet>
        <title>{page.title} | Proxy Emobility</title>
        <meta name="description" content={page.meta_description} />
        <link rel="canonical" href={`https://proxyemobility.netlify.app/${slug}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 to-primary-900 text-white py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          <Link
            to="/"
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              {getIcon()}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{page.title}</h1>
          </div>

          <p className="text-gray-300 text-sm">
            Dernière mise à jour: {new Date(page.updated_at).toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 lg:p-12">
              <div
                className="prose prose-lg max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-primary-800
                  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-primary-700
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                  prose-li:text-gray-700 prose-li:mb-2
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </div>

            {/* Footer Links */}
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              {slug !== 'legal' && (
                <Link
                  to="/legal"
                  className="inline-flex items-center px-4 py-2 text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <FileText size={18} className="mr-2" />
                  Mentions Légales
                </Link>
              )}
              {slug !== 'privacy' && (
                <Link
                  to="/privacy"
                  className="inline-flex items-center px-4 py-2 text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <Shield size={18} className="mr-2" />
                  Politique de Confidentialité
                </Link>
              )}
              {slug !== 'terms' && (
                <Link
                  to="/terms"
                  className="inline-flex items-center px-4 py-2 text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <Scale size={18} className="mr-2" />
                  Conditions d'Utilisation
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
